import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // AppBar
    'app.title': 'Sheet Analyst',
    'app.askAi': 'Ask AI',
    'app.closeChat': 'Close Chat',
    'app.settings': 'API Key & Model Settings',
    'app.darkMode': 'Switch to dark mode',
    'app.lightMode': 'Switch to light mode',
    // Upload
    'upload.title': 'AI Multi-Sheet Analyst',
    'upload.subtitle': 'Upload your spreadsheet or paste a Google Sheets link to get instant analysis, auto-generated charts, and AI-powered Q&A on your data.',
    'upload.drop': 'Drop your file here or click to browse',
    'upload.formats': 'Supports CSV, XLSX (including multi-sheet)',
    'upload.googleTitle': 'Import from Google Sheets',
    'upload.googleHint': 'Paste the URL of a publicly shared Google Sheet.',
    'upload.importBtn': 'Import Sheet',
    'upload.newFile': 'Upload New File',
    'upload.retry': 'Retry',
    // Sections
    'nav.overview': 'Overview',
    'nav.dashboard': 'Dashboard',
    'nav.export': 'Export',
    // Data Preview
    'preview.title': 'Data Preview',
    'preview.rows': 'rows',
    'preview.search': 'Search across all columns...',
    'preview.noResults': 'No rows match your search.',
    // Charts
    'charts.title': 'Auto-Generated Charts',
    'charts.noCharts': 'No charts generated',
    'charts.noChartsHint': "This dataset doesn't have suitable columns for automatic visualization.",
    'charts.download': 'Download PNG',
    'charts.fullscreen': 'Fullscreen',
    // Chat
    'chat.title': 'Chat with Data',
    'chat.querying': 'Querying:',
    'chat.askPlaceholder': 'Ask about {sheet}...',
    'chat.setApiKey': 'Set API Key to start chatting',
    'chat.askQuestion': 'Ask a question about your data',
    'chat.starters': 'Try these starter questions:',
    // Export
    'export.title': 'Export Summary',
    'export.includeAnalysis': 'Include analysis insights',
    'export.includeQA': 'Include Q&A highlights',
    // Analysis
    'analysis.deepAvailable': 'Deep Analysis Available',
    'analysis.deepHint': 'Get descriptive statistics, distribution insights, anomaly detection, and actionable recommendations.',
    'analysis.analyze': 'Analyze Sheet',
    'analysis.running': 'Running deep analysis...',
    'analysis.results': 'Analysis Results',
    'analysis.reanalyze': 'Re-analyze',
    'analysis.retry': 'Retry',
    // Footer
    'footer.builtWith': 'Built with',
    'footer.tagline': 'AI-powered spreadsheet analysis',
    'footer.cmdPalette': 'Command palette',
    // Comparison
    'compare.title': 'Sheet Comparison',
    'compare.metric': 'Metric',
    'compare.diff': 'Diff',
    'compare.same': 'Same',
    'compare.shared': 'shared',
    'compare.onlyIn': 'only in',
    'compare.columnOverlap': 'Column Overlap',
    // Confirm
    'confirm.cancel': 'Cancel',
    'confirm.replaceTitle': 'Replace current data?',
    'confirm.replaceDesc': 'Uploading a new file will replace the current dataset. Chat history will be preserved.',
    'confirm.replaceBtn': 'Replace',
    // General
    'general.loading': 'Loading...',
    'general.error': 'Error',
    // History
    'history.title': 'Analysis History',
    'history.noResults': 'No previous analyses found.',
    'history.active': 'Active',
    // Empty states
    'empty.noData': 'No data loaded',
    'empty.noDataHint': 'Upload a file or import a Google Sheet to begin.',
    'empty.noCharts': 'No charts available',
    'empty.noChat': 'Start a conversation',
    'empty.noChatHint': 'Ask questions about your data using AI.',
  },
  fr: {
    'app.title': 'Sheet Analyst',
    'app.askAi': 'Demander',
    'app.closeChat': 'Fermer',
    'app.settings': 'Cle API & Modele',
    'app.darkMode': 'Mode sombre',
    'app.lightMode': 'Mode clair',
    'upload.title': 'AI Multi-Sheet Analyst',
    'upload.subtitle': 'Importez votre tableur ou collez un lien Google Sheets pour obtenir une analyse instantanee, des graphiques auto-generes et un chat IA.',
    'upload.drop': 'Deposez votre fichier ici ou cliquez pour parcourir',
    'upload.formats': 'CSV, XLSX (multi-feuilles inclus)',
    'upload.googleTitle': 'Importer depuis Google Sheets',
    'upload.googleHint': "Collez l'URL d'une feuille Google partagee publiquement.",
    'upload.importBtn': 'Importer',
    'upload.newFile': 'Nouveau fichier',
    'upload.retry': 'Reessayer',
    'nav.overview': 'Apercu',
    'nav.dashboard': 'Tableau de bord',
    'nav.export': 'Exporter',
    'preview.title': 'Apercu des donnees',
    'preview.rows': 'lignes',
    'preview.search': 'Rechercher dans toutes les colonnes...',
    'preview.noResults': 'Aucune ligne ne correspond.',
    'charts.title': 'Graphiques auto-generes',
    'charts.noCharts': 'Aucun graphique genere',
    'charts.noChartsHint': "Ce jeu de donnees n'a pas de colonnes adaptees a la visualisation.",
    'charts.download': 'Telecharger PNG',
    'charts.fullscreen': 'Plein ecran',
    'chat.title': 'Chat avec les donnees',
    'chat.querying': 'Interroge:',
    'chat.askPlaceholder': 'Question sur {sheet}...',
    'chat.setApiKey': 'Configurez votre cle API',
    'chat.askQuestion': 'Posez une question sur vos donnees',
    'chat.starters': 'Essayez ces questions:',
    'export.title': 'Exporter le resume',
    'export.includeAnalysis': "Inclure les insights d'analyse",
    'export.includeQA': 'Inclure les Q&R',
    'analysis.deepAvailable': 'Analyse approfondie disponible',
    'analysis.deepHint': 'Statistiques descriptives, detection anomalies et recommandations.',
    'analysis.analyze': 'Analyser la feuille',
    'analysis.running': 'Analyse en cours...',
    'analysis.results': "Resultats de l'analyse",
    'analysis.reanalyze': 'Re-analyser',
    'analysis.retry': 'Reessayer',
    'footer.builtWith': 'Construit avec',
    'footer.tagline': 'Analyse de tableurs par IA',
    'footer.cmdPalette': 'Palette de commandes',
    'compare.title': 'Comparaison de feuilles',
    'compare.metric': 'Metrique',
    'compare.diff': 'Diff',
    'compare.same': 'Identique',
    'compare.shared': 'en commun',
    'compare.onlyIn': 'uniquement dans',
    'compare.columnOverlap': 'Chevauchement des colonnes',
    'confirm.cancel': 'Annuler',
    'confirm.replaceTitle': 'Remplacer les donnees?',
    'confirm.replaceDesc': "L'import d'un nouveau fichier remplacera le jeu de donnees actuel. L'historique du chat sera conserve.",
    'confirm.replaceBtn': 'Remplacer',
    'general.loading': 'Chargement...',
    'general.error': 'Erreur',
    'history.title': 'Historique des analyses',
    'history.noResults': 'Aucune analyse precedente.',
    'history.active': 'Actif',
    'empty.noData': 'Aucune donnee chargee',
    'empty.noDataHint': 'Importez un fichier ou collez un lien Google Sheets.',
    'empty.noCharts': 'Aucun graphique disponible',
    'empty.noChat': 'Demarrez une conversation',
    'empty.noChatHint': 'Posez des questions sur vos donnees avec l\'IA.',
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');

  const t = useCallback((key, vars = {}) => {
    let str = translations[lang]?.[key] || translations.en[key] || key;
    Object.entries(vars).forEach(([k, v]) => {
      str = str.replace(`{${k}}`, v);
    });
    return str;
  }, [lang]);

  const switchLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
  }, []);

  return (
    <I18nContext.Provider value={{ lang, t, switchLang, availableLangs: ['en', 'fr'] }}>
      {children}
    </I18nContext.Provider>
  );
};

export default function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback if not wrapped in provider - return English
    return {
      lang: 'en',
      t: (key) => translations.en[key] || key,
      switchLang: () => {},
      availableLangs: ['en', 'fr'],
    };
  }
  return ctx;
}
